import { nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import type { ComponentPublicInstance } from 'vue';

type MaybeEl = HTMLElement | null;
type MaybeComp = ComponentPublicInstance | null;
type AnyRef = Ref<MaybeEl | MaybeComp>;
type ElRef = Ref<HTMLElement | null>;

function toEl(v: MaybeEl | MaybeComp): HTMLElement | null {
  if (!v) return null;
  if (v instanceof HTMLElement) return v;
  const el = (v as ComponentPublicInstance).$el;
  return el instanceof HTMLElement ? el : null;
}

export function useViewportHeightMinus(args: {
  main: ElRef;
  minus: Array<{ label: string; ref: AnyRef }>;
  extra?: number;
  container?: ElRef;
  debug?: boolean;
  useDynamicViewport?: boolean;
}) {
  const debug = args.debug ?? true;

  function apply() {
    const mainEl = args.main.value;
    if (!mainEl) {
      if (debug) console.warn('[useViewportHeightMinus] main ref is null');
      return;
    }

    const vh =
      args.useDynamicViewport !== false
        ? (window.visualViewport?.height ?? window.innerHeight)
        : window.innerHeight;

    if (debug) {
      console.groupCollapsed('[useViewportHeightMinus] measure');
      console.log('viewportHeight:', vh);
    }

    let used = 0;

    for (const item of args.minus) {
      const el = toEl(item.ref.value);
      const h = el ? Math.round(el.getBoundingClientRect().height) : 0;

      if (debug) {
        console.log(`minus: ${item.label}`, {
          resolvedEl: el,
          height: h,
        });
      }

      used += h;
    }

    const extra = args.extra ?? 0;
    used += extra;

    const available = Math.max(0, vh - used);

    // page scrolls naturally; main just fills remaining viewport space at minimum
    mainEl.style.minHeight = `${available}px`;
    mainEl.style.height = '';

    if (debug) {
      console.log('sum minus heights:', used - extra);
      console.log('extra deducted:', extra);
      console.log('total used:', used);
      console.log('available(main min-height):', available);
      console.groupEnd();
    }
  }

  let ro: ResizeObserver | null = null;

  onMounted(async () => {
    await nextTick();
    apply();

    window.addEventListener('resize', apply, { passive: true });
    window.visualViewport?.addEventListener('resize', apply as any, {
      passive: true,
    });

    ro = new ResizeObserver(() => apply());

    for (const item of args.minus) {
      const el = toEl(item.ref.value);
      if (el) ro.observe(el);
    }

    if (args.main.value) ro.observe(args.main.value);
  });

  for (const item of args.minus) {
    watch(item.ref, async (nextVal, prevVal) => {
      await nextTick();
      apply();

      if (!ro) return;

      const prevEl = toEl(prevVal as any);
      const nextEl = toEl(nextVal as any);

      if (prevEl) ro.unobserve(prevEl);
      if (nextEl) ro.observe(nextEl);
    });
  }

  onBeforeUnmount(() => {
    window.removeEventListener('resize', apply);
    window.visualViewport?.removeEventListener('resize', apply as any);
    ro?.disconnect();
    ro = null;
  });

  return { apply };
}
